// The one utilisation Capacity and Talent show (owner, 2026-10-23): this org
// month to date from utilisation() — every project-timer hour ÷ the hours the
// person is there (contracted day × working days − approved leave, clamped to
// joining and exit), up to yesterday — banded against
// policy.finance.targetUtilisation. The server decides whom the caller sees: no
// row, or hours it withholds (not yours, not below you, no timesheets:full),
// is no figure ("—"), never 0.
import { useEffect, useState } from 'react';
import { isoDay, monthRange, orgToday } from './dates';
import {
  DEFAULT_TARGET_UTILISATION,
  loadTargetUtilisation,
  loadUtilisation,
  type UtilisationRow,
} from './finance';

/** Months read: this one, and five before it for the tiles' line and change. */
const MONTHS = 6;

export type PersonUtil = {
  /** Whole %; null with no available hours, or hours the caller can't read. */
  pct: number | null;
  /** Null: the server withholds this person's logged hours from the caller. */
  project: number | null;
  available: number;
  billable: number | null;
};

export type MonthUtilisation = {
  /** This org month's first day, and org today. */
  from: string;
  to: string;
  /** policy.finance.targetUtilisation, %. */
  target: number;
  byPerson: Map<string, PersonUtil>;
  /** Pooled Σ project ÷ Σ available per month, oldest first; the last is this month. */
  team: (number | null)[];
};

const pctOf = (project: number | null, available: number): number | null =>
  project != null && available > 0 ? Math.round((project / available) * 100) : null;

/** Red from 100%; amber from 85%, or from the target when that is higher; green below. */
export const utilBand = (pct: number, target: number): 'danger' | 'warning' | 'accent' =>
  pct >= 100 ? 'danger' : pct >= Math.max(85, target) ? 'warning' : 'accent';

/** utilisation() rows → each person's figure for `month`, and the studio's per month. */
export const summarise = (
  rows: readonly UtilisationRow[],
  months: readonly string[],
): Pick<MonthUtilisation, 'byPerson' | 'team'> => {
  const month = months.at(-1);
  const byPerson = new Map<string, PersonUtil>();
  for (const r of rows)
    if (r.month === month)
      byPerson.set(r.profile_id, {
        pct: pctOf(r.project_hours, r.available_hours),
        project: r.project_hours,
        available: r.available_hours,
        billable: r.billable_hours,
      });
  // The team pools only the people whose hours the caller can read, so a
  // withheld colleague neither drags it down nor shows through it.
  const team = months.map((m) => {
    const of = rows.filter((r) => r.month === m && r.project_hours != null);
    return of.length
      ? pctOf(
          of.reduce((a, r) => a + (r.project_hours ?? 0), 0),
          of.reduce((a, r) => a + r.available_hours, 0),
        )
      : null;
  });
  return { byPerson, team };
};

/** Null while loading. A failed read shows as no figures, never as 0%. */
export function useMonthUtilisation(tz: string): MonthUtilisation | null {
  const to = isoDay(orgToday(tz));
  const [state, setState] = useState<MonthUtilisation | null>(null);
  useEffect(() => {
    const today = new Date(`${to}T00:00:00`);
    const months = Array.from({ length: MONTHS }, (_, i) => monthRange(today, i - MONTHS + 1).from);
    let live = true;
    void Promise.all([loadUtilisation(months[0], to), loadTargetUtilisation()]).then(([u, t]) => {
      if (!live) return;
      setState({
        from: months[MONTHS - 1],
        to,
        target: (t.ok ? t.data : null) ?? DEFAULT_TARGET_UTILISATION,
        ...summarise(u.ok ? (u.data ?? []) : [], months),
      });
    });
    return () => {
      live = false;
    };
  }, [to]);
  return state;
}
